import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { provideRouter, withHashLocation } from '@angular/router'

import { DatePipe } from '@angular/common'
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import Aura from '@primeng/themes/aura'
import { SimpleNotificationsModule } from 'angular2-notifications'
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular'
import { InlineSVGModule } from 'ng-inline-svg-2'
import { ConfirmationService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { firstValueFrom } from 'rxjs'
import { routes } from './app.routes'
import { AppSpinnerService } from './shared/components/app-spinner/app-spinner.service'
import { ConfigService } from './shared/services/config.service'
import { handlerReducer } from './shared/store/handler/handler.reducer'
import { ForFundingEffects } from './shared/store/linking-for-funding/for-funding.effects'
import { forFundingReducer } from './shared/store/linking-for-funding/for-funding.reducer'
import { WithClientsEffects } from './shared/store/linking-with-clients/with-clients.effects'
import { withClientsReducer } from './shared/store/linking-with-clients/with-clients.reducer'
import { WithPNOColleaguesEffects } from './shared/store/linking-with-pno-colleagues/with-pno-colleagues.effects'
import { withPNOColleaguesReducer } from './shared/store/linking-with-pno-colleagues/with-pno-colleagues.reducer'
import { StageEffects } from './shared/store/stage/stage.effects'
import { stageReducer } from './shared/store/stage/stage.reducer'

export const appConfig: ApplicationConfig = {
   providers: [
      provideAnimationsAsync(),
      providePrimeNG({
         theme: {
            preset: Aura,
            options: {
               darkModeSelector: false || 'none',
               cssLayer: {
                  name: 'primeng',
                  order: 'tailwind-base, tailwind-utilities, primeng',
               },
            },
         },
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes, withHashLocation()),
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      DatePipe,
      DialogService,
      AppSpinnerService,
      {
         provide: DynamicDialogRef,
         useValue: [],
      },
      TranslateModule.forRoot({
         defaultLanguage: 'en-US',
         useDefaultLang: true,
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpBackend],
         },
      }).providers!,
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [TranslateService],
         useFactory: initializeTranslation,
      },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [ConfigService],
         useFactory: (configService: ConfigService) => {
            return () => configService.initRuntimeConfig()
         },
      },
      {
         provide: APP_INITIALIZER,
         useFactory: initializeKeycloak,
         multi: true,
         deps: [KeycloakService, HttpClient],
      },
      KeycloakService,
      ConfirmationService,
      { provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true },
      importProvidersFrom(
         InlineSVGModule.forRoot(),
         StoreModule.forRoot({
            stage: stageReducer,
            linkingForFunding: forFundingReducer,
            linkingWithClients: withClientsReducer,
            linkingWithPNOColleagues: withPNOColleaguesReducer,
            handler: handlerReducer,
         }),
         EffectsModule.forRoot([StageEffects, ForFundingEffects, WithClientsEffects, WithPNOColleaguesEffects]),
         StoreDevtoolsModule.instrument({
            maxAge: 25,
         }),
         SimpleNotificationsModule.forRoot({
            timeOut: 5000,
            showProgressBar: true,
            pauseOnHover: true,
            clickToClose: true,
            position: ['bottom', 'right'],
            lastOnBottom: true,
            maxStack: 3,
            maxLength: 500,
            theClass: 'app-notify',
            preventDuplicates: true,
         })
      ),
   ],
}

// required for AoT
export function HttpLoaderFactory(handler: HttpBackend) {
   return new TranslateHttpLoader(new HttpClient(handler))
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      // Usa en-US se la lingua del browser non è supportata
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return firstValueFrom(translate.use(langToUse))
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return () => {
      return firstValueFrom(httpClient.get<KeycloakConfig>(`assets/configs/properties.json?ts=${Date.now()}`)).then((kc: KeycloakConfig) => {
         return keycloak.init({
            config: {
               url: kc.keycloak.authUrl,
               realm: kc.keycloak.realm,
               clientId: kc.keycloak.clientId,
            },
            initOptions: { enableLogging: true, checkLoginIframe: false, flow: 'standard', onLoad: 'check-sso', scope: 'openid email profile' },
            enableBearerInterceptor: true,
            bearerPrefix: 'Bearer ',
         })
      })
   }
}

interface KeycloakConfig {
   keycloak: {
      authUrl: string
      realm: string
      clientId: string
   }
}
